import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import IosShare from '@mui/icons-material/IosShare';
import BookmarkBorder from '@mui/icons-material/BookmarkBorder';
import Bookmark from '@mui/icons-material/Bookmark';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import { feedsApi } from '../../api/feeds';
import { useUserStore } from '../../stores/userStore';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import { shareOrCopyUrl } from '../../utils/share';
import FeedBody from './FeedBody';
import { feedDate } from './feedPresentation';
export { feedDate } from './feedPresentation';

export default function FeedDetail() {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const userId = useUserStore((state) => state.user?.id);
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const requestKey = `${identifier}:${userId || 'guest'}:${attempt}`;
  const latestKey = useRef(requestKey);
  latestKey.current = requestKey;
  const post = result?.key === requestKey ? result.post : null;

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setMessage('');
    setBusy(false);
    feedsApi.detail(identifier, !!userId, controller.signal).then(
      (item) => {
        if (!controller.signal.aborted) setResult({ key: requestKey, post: item });
      },
      (failure) => {
        if (!controller.signal.aborted)
          setError({ key: requestKey, status: failure.response?.status });
      },
    );
    return () => controller.abort();
  }, [identifier, userId, requestKey]);

  const toggleBookmark = async () => {
    if (!userId) return saveCurrentPathAndRedirectToLogin(navigate);
    if (busy) return;
    setBusy(true);
    setMessage('');
    try {
      await feedsApi.bookmark(post.id, post.isBookmarked);
      if (latestKey.current === requestKey) {
        setResult({ key: requestKey, post: { ...post, isBookmarked: !post.isBookmarked } });
      }
    } catch {
      if (latestKey.current === requestKey) setMessage('저장하지 못했습니다. 다시 시도해 주세요.');
    } finally {
      if (latestKey.current === requestKey) setBusy(false);
    }
  };

  const share = async () => {
    try {
      const status = await shareOrCopyUrl({
        url: `/feed/${post.id}`,
        title: post.title,
        text: `${post.title} — 피드`,
      });
      if (latestKey.current === requestKey && status === 'copied')
        setMessage('공유 문구와 링크가 복사되었습니다.');
    } catch {
      if (latestKey.current === requestKey) setMessage('공유하지 못했습니다. 다시 시도해 주세요.');
    }
  };

  return (
    <PageLayout maxWidth="760px">
      <Typography variant="h6" align="center" sx={{ mb: 3 }}>
        피드
      </Typography>
      {error?.key === requestKey ? (
        <Stack spacing={2} alignItems="center">
          <Alert severity={error.status === 404 ? 'info' : 'error'}>
            {error.status === 404
              ? '게시물이 없거나 공개되지 않았습니다.'
              : '게시물을 불러오지 못했습니다.'}
          </Alert>
          {error.status !== 404 && (
            <Button onClick={() => setAttempt((n) => n + 1)}>다시 시도</Button>
          )}
          <Button component={RouterLink} to="/">
            홈으로
          </Button>
        </Stack>
      ) : !post ? (
        <Box role="status" aria-label="게시물 불러오는 중" sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Never derive metadata from an authenticated full article. The server
              publishes the anonymous excerpt; this page uses a safe fixed summary. */}
          <OpenInAppBanner type="feed" title={post.title} image={post.imageUrl} />
          <SEO
            title={post.title}
            description="피드에서 전체 이야기를 만나보세요."
            image={post.imageUrl}
            url={`${window.location.origin}/feed/${encodeURIComponent(post.slug || String(post.id))}`}
          />
          <Stack spacing={2} component="article">
            <Typography sx={{ color: '#F16438' }} variant="body2">
              피드
            </Typography>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 700, overflowWrap: 'anywhere' }}
            >
              {post.title}
            </Typography>
            <Typography color="text.secondary">{feedDate(post.createdAt)}</Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" startIcon={<IosShare />} onClick={share} sx={actionStyle}>
                공유
              </Button>
              <Button
                variant="outlined"
                startIcon={post.isBookmarked ? <Bookmark /> : <BookmarkBorder />}
                aria-pressed={post.isBookmarked}
                disabled={busy}
                onClick={toggleBookmark}
                sx={actionStyle}
              >
                {post.isBookmarked ? '저장됨' : '저장'}
              </Button>
            </Stack>
            {message && (
              <Alert severity="info" onClose={() => setMessage('')}>
                {message}
              </Alert>
            )}
            <Divider />
            <FeedBody key={requestKey} html={post.content} />
            {post.isContentLocked && (
              <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                <Typography variant="h6">로그인하고 계속 읽어보세요</Typography>
                <Typography color="text.secondary">
                  회원가입 후 피드의 전체 내용을 볼 수 있어요.
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#F16438',
                    color: '#fff',
                    borderRadius: 999,
                    minHeight: 44,
                    '&:hover': { bgcolor: '#df552e' },
                  }}
                  onClick={() => saveCurrentPathAndRedirectToLogin(navigate)}
                >
                  로그인 · 회원가입
                </Button>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </PageLayout>
  );
}

const actionStyle = {
  flex: 1,
  minHeight: 44,
  borderRadius: 999,
  color: 'text.primary',
  borderColor: 'divider',
};
